import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { 
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { 
  DollarSign, Calendar, Building, Globe, 
  Loader2, ChevronLeft, ChevronRight, Download, FileText
} from 'lucide-react';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { adminApi } from '../lib/api';

const CHANNEL_COLORS = {
  DIRECT: '#FFD700',
  PARKOS: '#3b82f6',
  PARKVIA: '#22c55e',
  ONEPARK: '#8b5cf6',
  LOOKING4PARKING: '#f97316',
  FREE2MOVE: '#ec4899'
};

const MONTHS = [
  { value: 1, es: 'Enero', en: 'January' },
  { value: 2, es: 'Febrero', en: 'February' },
  { value: 3, es: 'Marzo', en: 'March' },
  { value: 4, es: 'Abril', en: 'April' },
  { value: 5, es: 'Mayo', en: 'May' },
  { value: 6, es: 'Junio', en: 'June' },
  { value: 7, es: 'Julio', en: 'July' },
  { value: 8, es: 'Agosto', en: 'August' },
  { value: 9, es: 'Septiembre', en: 'September' },
  { value: 10, es: 'Octubre', en: 'October' },
  { value: 11, es: 'Noviembre', en: 'November' },
  { value: 12, es: 'Diciembre', en: 'December' }
];

const MonthlySummary = () => {
  const { i18n } = useTranslation();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const response = await adminApi.getMonthlySummary(year, month);
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const goToPreviousMonth = () => {
    if (month === 1) {
      setMonth(12);
      setYear(year - 1);
    } else {
      setMonth(month - 1);
    }
  };

  const goToNextMonth = () => {
    if (month === 12) {
      setMonth(1);
      setYear(year + 1);
    } else {
      setMonth(month + 1);
    }
  };

  const monthName = MONTHS.find(m => m.value === month)?.[i18n.language] || MONTHS[month - 1]?.es;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="monthly-summary">
      {/* Header with Month Selector */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white">
          {i18n.language === 'es' ? 'Resumen Mensual' : 'Monthly Summary'}
        </h2>
        
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={goToPreviousMonth}>
            <ChevronLeft className="w-5 h-5" />
          </Button>
          
          <div className="flex items-center gap-2 bg-white/10 rounded-lg px-4 py-2">
            <Select value={month.toString()} onValueChange={(v) => setMonth(parseInt(v))}>
              <SelectTrigger className="w-32 bg-transparent border-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card border-white/10">
                {MONTHS.map((m) => (
                  <SelectItem key={m.value} value={m.value.toString()}>
                    {i18n.language === 'es' ? m.es : m.en}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
              <SelectTrigger className="w-24 bg-transparent border-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card border-white/10">
                {[2024, 2025, 2026, 2027].map((y) => (
                  <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <Button variant="ghost" size="sm" onClick={goToNextMonth}>
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Total */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-primary/20 to-primary/5 rounded-xl p-6 border border-primary/30"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-primary/20 rounded-lg">
              <DollarSign className="w-6 h-6 text-primary" />
            </div>
            <div>
              <p className="text-sm text-zinc-400">{i18n.language === 'es' ? 'Total Facturado' : 'Total Revenue'}</p>
              <p className="text-3xl font-bold text-primary">
                {summary?.summary?.total_revenue_euros?.toFixed(2) || '0.00'}€
              </p>
            </div>
          </div>
          <p className="text-sm text-zinc-400">
            {summary?.summary?.total_reservations || 0} {i18n.language === 'es' ? 'reservas' : 'reservations'}
          </p>
        </motion.div>

        {/* Web/Direct */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white/5 rounded-xl p-6 border border-white/10"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-blue-500/20 rounded-lg">
              <Building className="w-6 h-6 text-blue-500" />
            </div>
            <div>
              <p className="text-sm text-zinc-400">{i18n.language === 'es' ? 'Web (Directas)' : 'Web (Direct)'}</p>
              <p className="text-2xl font-bold text-white">
                {summary?.summary?.web_revenue_euros?.toFixed(2) || '0.00'}€
              </p>
            </div>
          </div>
          <p className="text-sm text-zinc-400">
            {summary?.summary?.web_reservations || 0} {i18n.language === 'es' ? 'reservas' : 'reservations'}
          </p>
        </motion.div>

        {/* External Channels */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white/5 rounded-xl p-6 border border-white/10"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-green-500/20 rounded-lg">
              <Globe className="w-6 h-6 text-green-500" />
            </div>
            <div>
              <p className="text-sm text-zinc-400">{i18n.language === 'es' ? 'Plataformas Externas' : 'External Platforms'}</p>
              <p className="text-2xl font-bold text-white">
                {summary?.summary?.external_revenue_euros?.toFixed(2) || '0.00'}€
              </p>
            </div>
          </div>
          <p className="text-sm text-zinc-400">
            {summary?.summary?.external_reservations || 0} {i18n.language === 'es' ? 'reservas' : 'reservations'}
          </p>
        </motion.div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Channel Breakdown Pie */}
        <div className="bg-white/5 rounded-xl p-6 border border-white/10">
          <h3 className="text-white font-semibold mb-4">
            {i18n.language === 'es' ? 'Desglose por Canal' : 'Breakdown by Channel'}
          </h3>
          
          {summary?.by_channel?.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={summary.by_channel}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    innerRadius={40}
                    dataKey="total_cents"
                    nameKey="channel"
                    label={({ channel, percentage }) => `${channel} ${percentage}%`}
                    labelLine={false}
                  >
                    {summary.by_channel.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={CHANNEL_COLORS[entry.channel] || '#888'} 
                      />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value) => `${(value / 100).toFixed(2)}€`}
                    contentStyle={{ backgroundColor: '#111', border: '1px solid #333' }}
                  />
                </PieChart>
              </ResponsiveContainer>
              
              {/* Legend */}
              <div className="grid grid-cols-2 gap-2 mt-4">
                {summary.by_channel.map((c) => (
                  <div key={c.channel} className="flex items-center gap-2 text-sm">
                    <div 
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: CHANNEL_COLORS[c.channel] || '#888' }}
                    />
                    <span className="text-zinc-400">{c.channel}</span>
                    <span className="text-white ml-auto">{c.total_euros.toFixed(2)}€</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="text-zinc-500 text-center py-12">
              {i18n.language === 'es' ? 'Sin datos para este periodo' : 'No data for this period'}
            </p>
          )}
        </div>

        {/* Daily Chart */}
        <div className="bg-white/5 rounded-xl p-6 border border-white/10">
          <h3 className="text-white font-semibold mb-4">
            {i18n.language === 'es' ? 'Facturación Diaria' : 'Daily Revenue'}
          </h3>
          
          {summary?.by_day?.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={summary.by_day}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis 
                  dataKey="date" 
                  stroke="#888" 
                  fontSize={10}
                  tickFormatter={(value) => value.substring(8)}
                />
                <YAxis stroke="#888" fontSize={12} />
                <Tooltip 
                  formatter={(value) => `${value.toFixed(2)}€`}
                  labelFormatter={(label) => `${i18n.language === 'es' ? 'Día' : 'Day'}: ${label}`}
                  contentStyle={{ backgroundColor: '#111', border: '1px solid #333' }}
                />
                <Bar dataKey="total_euros" fill="#FFD700" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-zinc-500 text-center py-12">
              {i18n.language === 'es' ? 'Sin datos para este periodo' : 'No data for this period'}
            </p>
          )}
        </div>
      </div>

      {/* Detailed Table */}
      <div className="bg-white/5 rounded-xl p-6 border border-white/10">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold">
            {i18n.language === 'es' ? 'Detalle por Canal' : 'Channel Details'}
          </h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left text-zinc-400 text-sm py-3 px-4">Canal</th>
                <th className="text-right text-zinc-400 text-sm py-3 px-4">Reservas</th>
                <th className="text-right text-zinc-400 text-sm py-3 px-4">Total</th>
                <th className="text-right text-zinc-400 text-sm py-3 px-4">Media</th>
                <th className="text-right text-zinc-400 text-sm py-3 px-4">%</th>
              </tr>
            </thead>
            <tbody>
              {summary?.by_channel?.map((c) => (
                <tr key={c.channel} className="border-b border-white/5 hover:bg-white/5">
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: CHANNEL_COLORS[c.channel] || '#888' }}
                      />
                      <span className="text-white font-medium">{c.channel}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-right text-white">{c.count}</td>
                  <td className="py-3 px-4 text-right text-primary font-bold">{c.total_euros.toFixed(2)}€</td>
                  <td className="py-3 px-4 text-right text-zinc-400">{c.avg_euros.toFixed(2)}€</td>
                  <td className="py-3 px-4 text-right text-zinc-400">{c.percentage}%</td>
                </tr>
              ))}
              {summary?.by_channel?.length > 0 && (
                <tr className="bg-primary/10">
                  <td className="py-3 px-4 font-bold text-primary">TOTAL</td>
                  <td className="py-3 px-4 text-right text-white font-bold">{summary.summary.total_reservations}</td>
                  <td className="py-3 px-4 text-right text-primary font-bold">{summary.summary.total_revenue_euros.toFixed(2)}€</td>
                  <td className="py-3 px-4 text-right text-zinc-400">
                    {summary.summary.total_reservations > 0 
                      ? (summary.summary.total_revenue_euros / summary.summary.total_reservations).toFixed(2) 
                      : '0.00'}€
                  </td>
                  <td className="py-3 px-4 text-right text-white font-bold">100%</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MonthlySummary;
